import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'shared/icons';
import {
    FormatMinutesExact,
    FormatShortInstant,
    FormatUrgencyCutoff,
    TagChip,
    TodaySaoPauloIso,
} from '../../components/pedidos/pedidosShared';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useToast } from '../../components/ui/Toast';
import {
    CONCILIATION_CUTOFF_HINT,
    DATE_RULE_TAG_KEYS,
    GROUPED_RULE_KEYS,
    IsSlaRuleKey,
    ML_DEADLINE_TAGS,
    ROW_ONLY_RULE_NOTE,
    ROW_ONLY_TAG_KEYS,
    RULE_GROUPS,
    RULE_ONE_KEYS,
    RULE_UNIT_LABELS,
    SLA_RULE_TAG_KEYS,
    TAG_KIND_META,
    TONE_CLASSES,
    WEEKDAYS_ISO,
} from '../../constants/pedidoTags';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatIsoDate, ToIsoDate } from '../../utils/date';
import { FLAT_FIELD_CLASSES, PRIMARY_BUTTON_CLASSES, SECONDARY_BUTTON_CLASSES } from './listParts';

/**
 * Gestão de Pedidos — Regras (`/pedidos/regras`, rodada 3b + adendo do SLA,
 * 2026-09-23).
 *
 * Dois regimes de urgência por tag de AÇÃO:
 *   Regra 1 (prazo do ML) — horário e dias de atendimento (fuso de Brasília)
 *     e o CORTE: é urgente toda ação nossa com prazo do Mercado Livre até as
 *     N h do próximo dia de atendimento (ou já vencido). O corte "de agora"
 *     vem do servidor (`urgency_cutoff` de `GET /pedidos/regras`; fallback
 *     no summary) — a página não recalcula a regra.
 *   SLA de resposta (`sla_*_minutos`, em MINUTOS, relógio corrido) — passou
 *     do SLA sem resposta → urgente. Vazio = sem SLA. Padrões do backend
 *     (mensagem 15, reclamação/mediação/recontato 30, o resto sem SLA).
 *   Data de corte (`unit: 'date'`, rodada 5) — `conciliacao_data_corte`: só
 *     entram em "Conciliar/verificar caso" os casos encerrados a partir da
 *     data ('AAAA-MM-DD', fixa; vazio = sem corte; não pode ser futura).
 *   Seções próprias (`RULE_GROUPS`, 2026-09-29): Triagem (data de corte da
 *     triagem + SLAs de triagem/decisão), Financeiro (SLAs do apoio e da
 *     pendência) e Alerta do comprador (`unit` 'count' = casos e 'days' =
 *     dias — inteiros, não anuláveis). Essas keys saem das seções genéricas.
 * Valores, padrões, limites e textos vêm de `GET /pedidos/regras`; salvar é
 * por linha (`PUT /pedidos/regras/:key`, `orders:write`) e o erro de
 * validação aparece na própria linha, com "alterado por X em dd/mm hh:mm".
 * Sem `orders:write`, só leitura.
 */

const SECTION_LABEL = 'text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600';
const PANEL = 'border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950';
const HOURS_FIELD = `${FLAT_FIELD_CLASSES} w-16 text-center tabular-nums`;
// w-24: cabe o teto de 5 dígitos (43200 = 30 d) e o padrão 10080 (7 d) sem cortar.
const MINUTES_FIELD = `${FLAT_FIELD_CLASSES} w-24 text-center tabular-nums`;
const DATE_FIELD = `${FLAT_FIELD_CLASSES} w-40 tabular-nums`;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ERROR_TEXT = 'text-[11px] text-danger-600 dark:text-danger-400';
const META_TEXT = 'text-[11px] text-mono-400 dark:text-mono-500';
const DAYS_KEY = 'dias_atendimento';
// Tags que o SLA de resposta mede (para marcar as de prazo do ML que também têm SLA).
const TAGS_WITH_SLA = new Set(Object.values(SLA_RULE_TAG_KEYS).flat());
// Tags só de linha: o SLA/prazo delas não conta em Urgentes nem em Para fazer.
const ROW_ONLY_TAGS = new Set(ROW_ONLY_TAG_KEYS);
const isRowOnlyOnly = (tagKeys) => tagKeys.length > 0 && tagKeys.every((key) => ROW_ONLY_TAGS.has(key));

// ── Conversão valor do wire ⇄ rascunho do formulário ─────────────────────────

/** Regra de lista de dias (ISO 1..7) × regra de horas/minutos. */
function isDaysRule(rule) {
    return rule?.key === DAYS_KEY || Array.isArray(rule?.default);
}

/** SLA de resposta em MINUTOS (`unit: 'minutes'` do wire ou key `sla_*_minutos`). */
function isMinutesRule(rule) {
    return rule?.unit === 'minutes' || IsSlaRuleKey(rule?.key);
}

/** Unidade inteira nova ('count' = casos, 'days' = dias) → rótulos; senão null. */
function integerUnit(rule) {
    return RULE_UNIT_LABELS[rule?.unit] || null;
}

/** Regra de DATA ('AAAA-MM-DD' ou null): `unit: 'date'` do wire ou key conhecida. */
function isDateRule(rule) {
    return rule?.unit === 'date' || Object.hasOwn(DATE_RULE_TAG_KEYS, String(rule?.key || ''));
}

/** Valor de data do wire → 'AAAA-MM-DD' (aceita ISO com hora); vazio → null. */
function normalizeDateValue(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    return ToIsoDate(value) || String(value);
}

/** "= 1 h 30" ao lado do campo quando o SLA passa de uma hora; senão ''. */
function minutesEcho(draft) {
    const n = Number(String(draft ?? '').trim());
    return Number.isInteger(n) && n >= 60 ? `= ${FormatMinutesExact(n)}` : '';
}

function normalizeDays(value) {
    const list = Array.isArray(value) ? value : [];
    return [...new Set(list.map(Number).filter((n) => Number.isInteger(n) && n >= 1 && n <= 7))].sort((a, b) => a - b);
}

/** Valor do wire → rascunho: dias = array ordenado; data = 'AAAA-MM-DD'; horas = texto ('' = sem prazo). */
function toDraft(rule) {
    if (isDaysRule(rule)) {
        return normalizeDays(rule.value);
    }
    if (isDateRule(rule)) {
        return normalizeDateValue(rule.value) || '';
    }
    return rule?.value === null || rule?.value === undefined ? '' : String(rule.value);
}

/** Rascunho → valor do wire: '' vira null (sem prazo/corte); data fica texto; horas viram número. */
function fromDraft(rule, draft) {
    if (isDaysRule(rule)) {
        return normalizeDays(draft);
    }
    const text = String(draft ?? '').trim();
    if (text === '') {
        return null;
    }
    return isDateRule(rule) ? text : Number(text);
}

function sameValue(a, b) {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function isDirty(rule, draft) {
    if (!rule) {
        return false;
    }
    let current = rule.value ?? null;
    if (isDaysRule(rule)) {
        current = normalizeDays(rule.value);
    } else if (isDateRule(rule)) {
        current = normalizeDateValue(rule.value);
    }
    return !sameValue(fromDraft(rule, draft), current);
}

/**
 * Data de corte: 'AAAA-MM-DD' que existe no calendário, não futura (hoje no
 * fuso de Brasília) e dentro de `min`/`max` quando o wire mandar datas.
 */
function validateDate(rule, text) {
    const parsed = ISO_DATE_RE.test(text) ? ToIsoDate(new Date(`${text}T00:00:00Z`)) : null;
    if (parsed !== text) {
        return 'Use uma data válida (dd/mm/aaaa).';
    }
    if (text > TodaySaoPauloIso()) {
        return 'A data de corte não pode ser no futuro.';
    }
    const min = normalizeDateValue(rule.min);
    const max = normalizeDateValue(rule.max);
    if (min && ISO_DATE_RE.test(min) && text < min) {
        return `Use uma data a partir de ${FormatIsoDate(min)}.`;
    }
    if (max && ISO_DATE_RE.test(max) && text > max) {
        return `Use uma data até ${FormatIsoDate(max)}.`;
    }
    return '';
}

/** Validação local (o servidor valida de novo; a mensagem dele vence). */
function validateDraft(rule, draft) {
    if (isDaysRule(rule)) {
        return normalizeDays(draft).length ? '' : 'Escolha ao menos um dia de atendimento.';
    }
    const text = String(draft ?? '').trim();
    if (text === '') {
        return rule.nullable ? '' : 'Informe um valor.';
    }
    if (isDateRule(rule)) {
        return validateDate(rule, text);
    }
    const n = Number(text);
    if (!Number.isInteger(n)) {
        if (integerUnit(rule)) {
            return `Use um número inteiro de ${integerUnit(rule).long}.`;
        }
        return isMinutesRule(rule) ? 'Use minutos inteiros.' : 'Use horas inteiras.';
    }
    const hasMin = rule.min !== null && rule.min !== undefined;
    const hasMax = rule.max !== null && rule.max !== undefined;
    if ((hasMin && n < Number(rule.min)) || (hasMax && n > Number(rule.max))) {
        if (hasMin && hasMax) {
            return `Use um valor entre ${rule.min} e ${rule.max}.`;
        }
        return hasMin ? `Use um valor a partir de ${rule.min}.` : `Use um valor até ${rule.max}.`;
    }
    return '';
}

/** Erro do servidor → texto da linha (o `message` do backend é pt-BR). */
function saveErrorMessage(err) {
    if (err?.status === 403) {
        return 'Sem permissão para alterar as regras (orders:write).';
    }
    const detail = Array.isArray(err?.details) ? err.details.find((item) => item?.message)?.message : null;
    if (err?.message && err.message !== 'Payload validation failed') {
        return err.message;
    }
    return detail || 'Não foi possível salvar.';
}

/** "alterado por X em dd/mm hh:mm" — ou "padrão do sistema" quando nunca mudou. */
function changedLine(rules) {
    const changed = (Array.isArray(rules) ? rules : [])
        .filter((rule) => rule?.updated_at)
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];
    if (!changed) {
        return 'padrão do sistema (nunca alterado)';
    }
    const when = FormatShortInstant(changed.updated_at);
    return `alterado${changed.updated_by_name ? ` por ${changed.updated_by_name}` : ''}${when ? ` em ${when}` : ''}`;
}

function defaultText(rule) {
    if (isDaysRule(rule)) {
        const days = normalizeDays(rule.default);
        return days.map((iso) => WEEKDAYS_ISO.find((d) => d.iso === iso)?.short).join(' ');
    }
    if (isDateRule(rule)) {
        const iso = normalizeDateValue(rule.default);
        return iso ? FormatIsoDate(iso) : 'sem corte';
    }
    if (rule.default === null || rule.default === undefined) {
        return isMinutesRule(rule) ? 'sem SLA' : 'sem prazo';
    }
    if (integerUnit(rule)) {
        return `${rule.default} ${integerUnit(rule).short}`;
    }
    return isMinutesRule(rule) ? FormatMinutesExact(rule.default) || `${rule.default} min` : `${rule.default} h`;
}

// ── Peças ────────────────────────────────────────────────────────────────────

/** Campo numérico inline: horas (Regra 1) ou minutos (SLA — campo mais largo). */
const HoursInput = ({ rule, value, onChange, disabled, placeholder = '', label }) => (
    <input
        type="number"
        inputMode="numeric"
        step={1}
        min={rule?.min ?? undefined}
        max={rule?.max ?? undefined}
        value={value}
        placeholder={placeholder}
        disabled={disabled || !rule}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
        className={isMinutesRule(rule) || integerUnit(rule) ? MINUTES_FIELD : HOURS_FIELD}
    />
);

/** Dias de atendimento: sete botões seg…dom (toggle); ativo = preto. */
const DaysPicker = ({ value, onChange, disabled }) => {
    const selected = new Set(normalizeDays(value));
    return (
        <span className="inline-flex flex-wrap gap-px bg-mono-200 p-px align-middle dark:bg-mono-800">
            {WEEKDAYS_ISO.map((day) => {
                const on = selected.has(day.iso);
                return (
                    <button
                        key={day.iso}
                        type="button"
                        aria-pressed={on}
                        title={day.label}
                        disabled={disabled}
                        onClick={() => {
                            const next = new Set(selected);
                            if (on) {
                                next.delete(day.iso);
                            } else {
                                next.add(day.iso);
                            }
                            onChange(normalizeDays([...next]));
                        }}
                        className={`w-10 px-1.5 py-1 text-center text-[12px] transition-colors disabled:cursor-default ${
                            on
                                ? 'bg-black text-white dark:bg-white dark:text-black'
                                : 'bg-white text-mono-400 hover:text-black disabled:hover:text-mono-400 dark:bg-mono-950 dark:text-mono-500 dark:hover:text-white'
                        }`}
                    >
                        {day.short}
                    </button>
                );
            })}
        </span>
    );
};

/**
 * Regra 1 em destaque (borda red: é a regra que decide o vermelho dos prazos
 * do ML): a frase com os quatro campos + "Agora: urgente = prazo do ML até …"
 * do servidor. As quatro keys salvam juntas (um botão), cada erro na frente
 * do seu campo.
 */
const RuleOneCard = ({ rulesByKey, drafts, errors, onDraft, onSave, onDiscard, saving, busy, canEdit, cutoff }) => {
    const [startRule, endRule, daysRule, cutRule] = RULE_ONE_KEYS.map((key) => rulesByKey[key] || null);
    const present = [startRule, endRule, daysRule, cutRule].filter(Boolean);
    const dirty = present.some((rule) => isDirty(rule, drafts[rule.key]));
    const editable = canEdit && !busy;
    const fieldErrors = RULE_ONE_KEYS.map((key) => errors[key]).filter(Boolean);
    const hours = (rule, key, label) =>
        rule ? (
            <HoursInput
                rule={rule}
                value={drafts[key] ?? ''}
                onChange={(v) => onDraft(key, v)}
                disabled={!editable}
                label={label}
            />
        ) : (
            <span className="font-mono text-mono-400">—</span>
        );

    let now;
    if (cutoff.loading) {
        now = <span className="text-mono-400 dark:text-mono-500">calculando…</span>;
    } else if (cutoff.error) {
        now = <span className="text-mono-400 dark:text-mono-500">não foi possível calcular agora</span>;
    } else if (!cutoff.iso) {
        now = <span className="text-mono-400 dark:text-mono-500">— (o servidor ainda não informa o corte)</span>;
    } else {
        now = <span className="text-danger-700 dark:text-danger-300">{FormatUrgencyCutoff(cutoff.iso)}</span>;
    }

    return (
        <section className={`${PANEL} border-l-4 ${TONE_CLASSES.red.border} px-5 py-4`}>
            <p className={SECTION_LABEL}>Regra 1 · Urgência pelo prazo do Mercado Livre</p>
            <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2 text-[15px] leading-relaxed text-mono-800 dark:text-mono-100">
                <span>Atendimento das</span>
                {hours(startRule, 'atendimento_inicio_hora', 'Início do atendimento (hora)')}
                <span>h às</span>
                {hours(endRule, 'atendimento_fim_hora', 'Fim do atendimento (hora)')}
                <span>h, dias</span>
                {daysRule ? (
                    <DaysPicker
                        value={drafts[DAYS_KEY] ?? []}
                        onChange={(v) => onDraft(DAYS_KEY, v)}
                        disabled={!editable}
                    />
                ) : (
                    <span className="font-mono text-mono-400">—</span>
                )}
                <span>— é urgente toda ação nossa com prazo do ML até as</span>
                {hours(cutRule, 'urgencia_corte_hora', 'Hora do corte da urgência')}
                <span>h do próximo dia de atendimento.</span>
            </p>
            <p className="mt-3 text-sm text-mono-600 dark:text-mono-300">
                <span className="text-mono-400 dark:text-mono-500">Agora:</span> urgente = prazo do ML até {now}
            </p>
            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                Vale para os prazos do Mercado Livre (lista no fim da página). O SLA de resposta, abaixo, tem regra
                própria e não passa por este corte.
            </p>
            {fieldErrors.length ? (
                <ul className="mt-2 flex flex-col gap-0.5" role="alert">
                    {fieldErrors.map((message) => (
                        <li key={message} className={ERROR_TEXT}>
                            {message}
                        </li>
                    ))}
                </ul>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center gap-3">
                {canEdit ? (
                    <>
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={!dirty || busy}
                            className={PRIMARY_BUTTON_CLASSES}
                        >
                            {saving ? 'Salvando…' : 'Salvar'}
                        </button>
                        {dirty && !busy ? (
                            <button type="button" onClick={onDiscard} className={SECONDARY_BUTTON_CLASSES}>
                                Descartar
                            </button>
                        ) : null}
                    </>
                ) : null}
                <span className={META_TEXT}>{changedLine(present)}</span>
            </div>
            <p className={`mt-3 ${META_TEXT}`}>
                Fuso de Brasília. O corte é no primeiro dia de atendimento DEPOIS de hoje (ex., de segunda a sexta: numa
                sexta ou no fim de semana, o corte cai na segunda). Início e fim do atendimento são informativos; quem
                decide a urgência é o corte. Feriados não são considerados.
            </p>
        </section>
    );
};

/**
 * Uma linha de SLA de resposta: título, tags medidas, campo em MINUTOS (com
 * "= 1 h 30" quando passa de uma hora), "sem SLA" quando vazio, salvar.
 * Regra de outra unidade (horas) que o backend venha a mandar cai aqui com o
 * rótulo certo. `saving` = esta linha salvando; `busy` = alguma gravação em
 * curso na página.
 */
const SlaRow = ({ rule, draft, error, onDraft, onSave, onClear, saving, busy, canEdit }) => {
    const dirty = isDirty(rule, draft);
    const tagKeys = SLA_RULE_TAG_KEYS[rule.key] || [];
    const editable = canEdit && !busy;
    const empty = String(draft ?? '').trim() === '';
    const minutes = isMinutesRule(rule);
    const integer = integerUnit(rule);
    const noValue = minutes ? 'sem SLA' : 'sem prazo';
    const unitText = integer ? integer.short : minutes ? 'min' : 'horas';
    const unitLong = integer ? integer.long : minutes ? 'minutos' : 'horas';
    const echo = minutes ? minutesEcho(draft) : '';
    return (
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 border-t border-mono-100 px-5 py-3 first:border-t-0 md:grid-cols-[minmax(0,1fr)_auto] dark:border-mono-900">
            <div className="min-w-0">
                <p className="text-sm text-black dark:text-white">{rule.title || rule.key}</p>
                {rule.description ? (
                    <p className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">{rule.description}</p>
                ) : null}
                {tagKeys.length ? (
                    <p className="mt-1.5 flex flex-wrap gap-1">
                        {tagKeys.map((key) => (
                            <TagChip key={key} tagKey={key} />
                        ))}
                    </p>
                ) : null}
                {isRowOnlyOnly(tagKeys) ? (
                    <p className="mt-1 text-xs text-mono-600 dark:text-mono-300">{ROW_ONLY_RULE_NOTE}</p>
                ) : null}
            </div>
            <div className="flex flex-col items-start gap-1 md:items-end">
                <div className="flex flex-wrap items-center gap-2">
                    <HoursInput
                        rule={rule}
                        value={draft ?? ''}
                        placeholder="—"
                        onChange={(v) => onDraft(rule.key, v)}
                        disabled={!editable}
                        label={`${rule.title || rule.key} (${unitLong})`}
                    />
                    <span className="w-24 text-xs tabular-nums text-mono-500 dark:text-mono-400">
                        {empty ? noValue : `${unitText}${echo ? ` ${echo}` : ''}`}
                    </span>
                    {canEdit ? (
                        <>
                            {rule.nullable && !empty ? (
                                <button
                                    type="button"
                                    onClick={() => onClear(rule.key)}
                                    disabled={busy}
                                    title={
                                        minutes ? 'Salvar sem SLA (só urgente pelo prazo do ML, se houver)' : undefined
                                    }
                                    className="px-1 text-[11px] text-mono-500 transition-colors hover:text-black disabled:opacity-50 dark:hover:text-white"
                                >
                                    {noValue}
                                </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => onSave(rule.key)}
                                disabled={!dirty || busy}
                                className={dirty ? PRIMARY_BUTTON_CLASSES : SECONDARY_BUTTON_CLASSES}
                            >
                                {saving ? 'Salvando…' : 'Salvar'}
                            </button>
                        </>
                    ) : null}
                </div>
                <span className={META_TEXT}>
                    padrão: {defaultText(rule)} · {changedLine([rule])}
                </span>
                {error ? (
                    <span role="alert" className={ERROR_TEXT}>
                        {error}
                    </span>
                ) : null}
            </div>
        </div>
    );
};

/**
 * Uma regra de DATA (rodada 5 — `conciliacao_data_corte`): título e descrição
 * do backend, tags que ela limita, campo de data (teto = hoje em Brasília),
 * "sem corte" quando vazio, salvar por linha. A frase fixa da conciliação só
 * aparece quando a descrição do backend ainda não a diz.
 */
const DateRuleRow = ({ rule, draft, error, onDraft, onSave, onClear, saving, busy, canEdit }) => {
    const dirty = isDirty(rule, draft);
    const tagKeys = DATE_RULE_TAG_KEYS[rule.key] || [];
    const editable = canEdit && !busy;
    const empty = String(draft ?? '').trim() === '';
    const hint = tagKeys.includes('caso_para_conciliar') ? CONCILIATION_CUTOFF_HINT : '';
    const triage = rule.key === 'triagem_data_corte';
    const showHint = hint && !String(rule.description || '').includes(hint.replace(/\.$/, ''));
    const maxIso = normalizeDateValue(rule.max);
    const today = TodaySaoPauloIso();
    return (
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 border-t border-mono-100 px-5 py-3 first:border-t-0 md:grid-cols-[minmax(0,1fr)_auto] dark:border-mono-900">
            <div className="min-w-0">
                <p className="text-sm text-black dark:text-white">{rule.title || rule.key}</p>
                {showHint ? <p className="mt-0.5 text-xs text-mono-700 dark:text-mono-200">{hint}</p> : null}
                {rule.description ? (
                    <p className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">{rule.description}</p>
                ) : null}
                {tagKeys.length ? (
                    <p className="mt-1.5 flex flex-wrap gap-1">
                        {tagKeys.map((key) => (
                            <TagChip key={key} tagKey={key} />
                        ))}
                    </p>
                ) : null}
            </div>
            <div className="flex flex-col items-start gap-1 md:items-end">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-mono-500 dark:text-mono-400">a partir de</span>
                    <input
                        type="date"
                        value={draft ?? ''}
                        max={maxIso && maxIso < today ? maxIso : today}
                        min={normalizeDateValue(rule.min) || undefined}
                        disabled={!editable}
                        aria-label={rule.title || rule.key}
                        onChange={(e) => onDraft(rule.key, e.target.value)}
                        className={DATE_FIELD}
                    />
                    {empty ? <span className="text-xs text-mono-500 dark:text-mono-400">sem corte</span> : null}
                    {canEdit ? (
                        <>
                            {rule.nullable && !empty ? (
                                <button
                                    type="button"
                                    onClick={() => onClear(rule.key)}
                                    disabled={busy}
                                    title={
                                        triage
                                            ? 'Salvar sem data de corte (todos os casos entram na triagem)'
                                            : 'Salvar sem data de corte (todos os casos encerrados entram)'
                                    }
                                    className="px-1 text-[11px] text-mono-500 transition-colors hover:text-black disabled:opacity-50 dark:hover:text-white"
                                >
                                    sem corte
                                </button>
                            ) : null}
                            <button
                                type="button"
                                onClick={() => onSave(rule.key)}
                                disabled={!dirty || busy}
                                className={dirty ? PRIMARY_BUTTON_CLASSES : SECONDARY_BUTTON_CLASSES}
                            >
                                {saving ? 'Salvando…' : 'Salvar'}
                            </button>
                        </>
                    ) : null}
                </div>
                <span className={META_TEXT}>
                    padrão: {defaultText(rule)} · {changedLine([rule])}
                </span>
                {error ? (
                    <span role="alert" className={ERROR_TEXT}>
                        {error}
                    </span>
                ) : null}
            </div>
        </div>
    );
};

// ── Página ───────────────────────────────────────────────────────────────────

const Regras = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { checkPermission } = useAuth();
    const canEdit = checkPermission('orders:write');

    const [rules, setRules] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [errors, setErrors] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    const [cutoff, setCutoff] = useState({ loading: true, iso: null, error: false });

    const rulesSeq = useRef(0);
    const cutoffSeq = useRef(0);
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const backTo =
        typeof location.state?.from === 'string' && location.state.from.startsWith('/pedidos')
            ? location.state.from
            : '/pedidos';

    // Fallback do corte "de agora": o summary (mesmo cálculo que pinta a
    // lista) — só quando `GET /pedidos/regras` não trouxer `urgency_cutoff`.
    const loadCutoffFromSummary = useCallback(async () => {
        cutoffSeq.current += 1;
        const seq = cutoffSeq.current;
        setCutoff((prev) => ({ ...prev, loading: true, error: false }));
        try {
            const data = await api.getPedidosSummary({});
            if (seq === cutoffSeq.current) {
                setCutoff({ loading: false, iso: data?.urgency_cutoff || null, error: false });
            }
        } catch {
            if (seq === cutoffSeq.current) {
                setCutoff({ loading: false, iso: null, error: true });
            }
        }
    }, []);

    // `keepDrafts` = keys cujo rascunho sobrevive ao recarregar (edição em
    // curso em OUTRA linha não se perde quando uma linha salva). O corte "de
    // agora" vem na mesma resposta (`urgency_cutoff`) — salvar a Regra 1 e
    // recarregar já traz o corte novo.
    const loadRules = useCallback(
        async ({ keepDrafts = null } = {}) => {
            rulesSeq.current += 1;
            const seq = rulesSeq.current;
            setLoading(true);
            try {
                const data = await api.getPedidoRegras();
                if (seq !== rulesSeq.current) {
                    return;
                }
                const list = Array.isArray(data?.rules) ? data.rules : [];
                setRules(list);
                setDrafts((prev) => {
                    const next = {};
                    for (const rule of list) {
                        next[rule.key] =
                            keepDrafts?.has(rule.key) && Object.hasOwn(prev, rule.key) ? prev[rule.key] : toDraft(rule);
                    }
                    return next;
                });
                setLoadError(null);
                if (data?.urgency_cutoff) {
                    cutoffSeq.current += 1;
                    setCutoff({ loading: false, iso: data.urgency_cutoff, error: false });
                } else {
                    loadCutoffFromSummary();
                }
            } catch (err) {
                if (seq === rulesSeq.current) {
                    setLoadError(err?.message || 'Não foi possível carregar as regras.');
                    setCutoff((prev) => ({ ...prev, loading: false }));
                }
            } finally {
                if (seq === rulesSeq.current) {
                    setLoading(false);
                }
            }
        },
        [loadCutoffFromSummary],
    );

    useEffect(() => {
        loadRules();
    }, [loadRules]);

    const rulesByKey = useMemo(() => Object.fromEntries((rules || []).map((rule) => [rule.key, rule])), [rules]);
    // Tudo que não é da Regra 1 nem data de corte é SLA de resposta (minutos);
    // uma regra de outra unidade que o backend venha a mandar aparece na mesma
    // lista com o rótulo da unidade dela. Datas de corte têm seção própria.
    // Keys das seções próprias (Triagem, Financeiro, Alerta do comprador —
    // `RULE_GROUPS`) saem das seções genéricas.
    const slaRules = useMemo(
        () =>
            (rules || []).filter(
                (rule) =>
                    !RULE_ONE_KEYS.includes(rule.key) && !isDateRule(rule) && !GROUPED_RULE_KEYS.includes(rule.key),
            ),
        [rules],
    );
    const dateRules = useMemo(
        () => (rules || []).filter((rule) => isDateRule(rule) && !GROUPED_RULE_KEYS.includes(rule.key)),
        [rules],
    );
    const ruleGroups = useMemo(
        () =>
            RULE_GROUPS.map((group) => ({
                ...group,
                rules: group.keys.map((key) => rulesByKey[key]).filter(Boolean),
            })).filter((group) => group.rules.length),
        [rulesByKey],
    );

    const setDraft = (key, value) => {
        setDrafts((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => {
            if (!prev[key]) {
                return prev;
            }
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const otherDirtyKeys = (exclude) =>
        new Set(
            (rules || [])
                .filter((rule) => !exclude.includes(rule.key) && isDirty(rule, drafts[rule.key]))
                .map((rule) => rule.key),
        );

    // Regra 1: valida as quatro, salva só as alteradas, em sequência; o
    // primeiro erro para a fila e fica na frente do campo.
    const saveRuleOne = async () => {
        const present = RULE_ONE_KEYS.map((key) => rulesByKey[key]).filter(Boolean);
        const nextErrors = {};
        for (const rule of present) {
            const message = validateDraft(rule, drafts[rule.key]);
            if (message) {
                nextErrors[rule.key] = `${rule.title || rule.key}: ${message}`;
            }
        }
        const start = fromDraft(rulesByKey.atendimento_inicio_hora, drafts.atendimento_inicio_hora);
        const end = fromDraft(rulesByKey.atendimento_fim_hora, drafts.atendimento_fim_hora);
        if (!nextErrors.atendimento_fim_hora && start !== null && end !== null && end <= start) {
            nextErrors.atendimento_fim_hora = 'O fim do atendimento precisa ser depois do início.';
        }
        if (Object.keys(nextErrors).length) {
            setErrors((prev) => ({ ...prev, ...nextErrors }));
            return;
        }
        const changed = present.filter((rule) => isDirty(rule, drafts[rule.key]));
        if (!changed.length) {
            return;
        }
        setSavingKey('regra1');
        let saved = 0;
        try {
            for (const rule of changed) {
                try {
                    await api.updatePedidoRegra(rule.key, fromDraft(rule, drafts[rule.key]));
                    saved += 1;
                } catch (err) {
                    setErrors((prev) => ({
                        ...prev,
                        [rule.key]: `${rule.title || rule.key}: ${saveErrorMessage(err)}`,
                    }));
                    break;
                }
            }
        } finally {
            setSavingKey(null);
        }
        if (saved) {
            if (saved === changed.length) {
                toastRef.current.success('Regra 1 salva.');
            }
            // Recarrega do servidor (valor canônico + "alterado por" + corte
            // novo); o que falhou continua no rascunho para corrigir.
            const failed = changed.slice(saved).map((rule) => rule.key);
            await loadRules({ keepDrafts: new Set([...otherDirtyKeys(RULE_ONE_KEYS), ...failed]) });
        }
    };

    const discardRuleOne = () => {
        setDrafts((prev) => {
            const next = { ...prev };
            for (const key of RULE_ONE_KEYS) {
                if (rulesByKey[key]) {
                    next[key] = toDraft(rulesByKey[key]);
                }
            }
            return next;
        });
        setErrors((prev) => {
            const next = { ...prev };
            for (const key of RULE_ONE_KEYS) {
                delete next[key];
            }
            return next;
        });
    };

    const saveSla = async (key, override = undefined) => {
        const rule = rulesByKey[key];
        if (!rule) {
            return;
        }
        const draft = override === undefined ? drafts[key] : override;
        const message = validateDraft(rule, draft);
        if (message) {
            setErrors((prev) => ({ ...prev, [key]: message }));
            return;
        }
        setSavingKey(key);
        try {
            await api.updatePedidoRegra(key, fromDraft(rule, draft));
        } catch (err) {
            setErrors((prev) => ({ ...prev, [key]: saveErrorMessage(err) }));
            setSavingKey(null);
            return;
        }
        setSavingKey(null);
        toastRef.current.success(`${rule.title || key}: salvo.`);
        await loadRules({ keepDrafts: otherDirtyKeys([key]) });
    };

    // "sem SLA" / "sem corte": limpa o campo e salva na hora (é a ação completa).
    const clearSla = (key) => {
        setDraft(key, '');
        saveSla(key, '');
    };

    const backButton = (
        <button
            type="button"
            onClick={() => navigate(backTo)}
            className="inline-flex items-center gap-1 text-sm text-mono-500 transition-colors hover:text-black dark:text-mono-400 dark:hover:text-white"
        >
            <ArrowLeft size={16} /> Pedidos
        </button>
    );

    return (
        <div className="flex w-full max-w-5xl flex-col gap-4">
            {/* ── Topo: voltar + módulo ── */}
            <div className="flex flex-wrap items-center gap-3">
                {backButton}
                <span className={SECTION_LABEL}>Gestão de Pedidos · Regras</span>
            </div>

            {/* ── Para fazer × Urgente × SLA ── */}
            <section className={`${PANEL} px-5 py-4`}>
                <dl className="grid grid-cols-1 gap-x-8 gap-y-3 text-sm md:grid-cols-3">
                    <div>
                        <dt className="text-[10px] font-bold uppercase tracking-widest text-black dark:text-white">
                            {TAG_KIND_META.acao.label}
                        </dt>
                        <dd className="mt-1 text-mono-600 dark:text-mono-300">
                            Tag de ação: a vez é nossa (responder, etiquetar, inspecionar, despachar, conciliar…). É o
                            número grande dos blocos do topo (pendências). As tags de "
                            {TAG_KIND_META.acompanhamento.label}" são estado de outra parte e nunca são urgentes.
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[10px] font-bold uppercase tracking-widest text-danger-600 dark:text-danger-400">
                            Urgente
                        </dt>
                        <dd className="mt-1 text-mono-600 dark:text-mono-300">
                            Ação para fazer (1) com prazo do Mercado Livre até o corte da Regra 1 — ou já vencido — ou
                            (2) que passou do SLA de resposta. Nos blocos do topo, é o "N urgentes" em vermelho ao lado
                            das pendências.
                        </dd>
                    </div>
                    <div>
                        <dt className="text-[10px] font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                            SLA de resposta
                        </dt>
                        <dd className="mt-1 text-mono-600 dark:text-mono-300">
                            Nosso tempo para agir, em minutos corridos desde que a ação ficou nossa. Vale junto com o
                            prazo do ML: o que disparar primeiro deixa a ação urgente.
                        </dd>
                    </div>
                </dl>
            </section>

            {!canEdit ? (
                <p className="border border-mono-200 bg-mono-50 px-4 py-2 text-xs text-mono-500 dark:border-mono-800 dark:bg-mono-900/40 dark:text-mono-400">
                    Somente leitura: alterar as regras exige a permissão de edição de pedidos (orders:write).
                </p>
            ) : null}

            {!rules && loading ? (
                <LoadingSpinner className="py-16" />
            ) : !rules && loadError ? (
                <div className="border border-dashed border-danger-300 py-10 text-center dark:border-danger-700">
                    <p className="text-sm text-danger-700 dark:text-danger-300">{loadError}</p>
                    <button type="button" onClick={() => loadRules()} className={`${SECONDARY_BUTTON_CLASSES} mt-3`}>
                        Tentar novamente
                    </button>
                </div>
            ) : (
                <>
                    <RuleOneCard
                        rulesByKey={rulesByKey}
                        drafts={drafts}
                        errors={errors}
                        onDraft={setDraft}
                        onSave={saveRuleOne}
                        onDiscard={discardRuleOne}
                        saving={savingKey === 'regra1'}
                        busy={savingKey !== null}
                        canEdit={canEdit}
                        cutoff={cutoff}
                    />

                    <section className={PANEL}>
                        <div className="border-b border-mono-100 px-5 py-3 dark:border-mono-900">
                            <p className={SECTION_LABEL}>SLA de resposta</p>
                            <p className="mt-1 text-xs text-mono-600 dark:text-mono-300">
                                Passou do SLA sem resposta → urgente.
                            </p>
                            <p className="mt-0.5 text-xs text-mono-500 dark:text-mono-400">
                                Em minutos, relógio corrido desde que a ação ficou nossa (o que chega à noite ou no fim
                                de semana já nasce atrasado no começo do expediente). Vazio = sem SLA: a ação aparece em
                                "Para fazer" e só fica urgente pelo prazo do ML, se tiver.
                            </p>
                        </div>
                        {slaRules.length ? (
                            slaRules.map((rule) => (
                                <SlaRow
                                    key={rule.key}
                                    rule={rule}
                                    draft={drafts[rule.key]}
                                    error={errors[rule.key]}
                                    onDraft={setDraft}
                                    onSave={saveSla}
                                    onClear={clearSla}
                                    saving={savingKey === rule.key}
                                    busy={savingKey !== null}
                                    canEdit={canEdit}
                                />
                            ))
                        ) : (
                            <p className="px-5 py-4 text-xs text-mono-500 dark:text-mono-400">
                                Nenhum SLA configurável.
                            </p>
                        )}
                    </section>

                    {dateRules.length ? (
                        <section className={PANEL}>
                            <div className="border-b border-mono-100 px-5 py-3 dark:border-mono-900">
                                <p className={SECTION_LABEL}>Data de corte</p>
                                <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                    Data fixa: o que ficou antes dela não entra na tag nem nas contagens (a ação
                                    continua disponível no caso). Vazio = sem corte.
                                </p>
                            </div>
                            {dateRules.map((rule) => (
                                <DateRuleRow
                                    key={rule.key}
                                    rule={rule}
                                    draft={drafts[rule.key]}
                                    error={errors[rule.key]}
                                    onDraft={setDraft}
                                    onSave={saveSla}
                                    onClear={clearSla}
                                    saving={savingKey === rule.key}
                                    busy={savingKey !== null}
                                    canEdit={canEdit}
                                />
                            ))}
                        </section>
                    ) : null}

                    {ruleGroups.map((group) => (
                        <section key={group.key} className={PANEL}>
                            <div className="border-b border-mono-100 px-5 py-3 dark:border-mono-900">
                                <p className={SECTION_LABEL}>{group.title}</p>
                                <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{group.description}</p>
                            </div>
                            {group.rules.map((rule) =>
                                isDateRule(rule) ? (
                                    <DateRuleRow
                                        key={rule.key}
                                        rule={rule}
                                        draft={drafts[rule.key]}
                                        error={errors[rule.key]}
                                        onDraft={setDraft}
                                        onSave={saveSla}
                                        onClear={clearSla}
                                        saving={savingKey === rule.key}
                                        busy={savingKey !== null}
                                        canEdit={canEdit}
                                    />
                                ) : (
                                    <SlaRow
                                        key={rule.key}
                                        rule={rule}
                                        draft={drafts[rule.key]}
                                        error={errors[rule.key]}
                                        onDraft={setDraft}
                                        onSave={saveSla}
                                        onClear={clearSla}
                                        saving={savingKey === rule.key}
                                        busy={savingKey !== null}
                                        canEdit={canEdit}
                                    />
                                ),
                            )}
                        </section>
                    ))}

                    <section className={PANEL}>
                        <div className="border-b border-mono-100 px-5 py-3 dark:border-mono-900">
                            <p className={SECTION_LABEL}>Prazos do Mercado Livre · Regra 1</p>
                            <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                                Vêm do próprio caso/envio no ML — não se configuram aqui. A Regra 1 vale para estes:
                                urgente quando o prazo vence até o corte.
                            </p>
                        </div>
                        {ML_DEADLINE_TAGS.map((item) => (
                            <div
                                key={item.key}
                                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-mono-100 px-5 py-2 first:border-t-0 dark:border-mono-900"
                            >
                                <TagChip tagKey={item.key} />
                                <span className="text-xs text-mono-500 dark:text-mono-400">
                                    ML: {item.source}
                                    {TAGS_WITH_SLA.has(item.key) ? ' · também tem SLA de resposta (acima)' : ''}
                                    {ROW_ONLY_TAGS.has(item.key) ? ` · ${ROW_ONLY_RULE_NOTE}` : ''}
                                </span>
                            </div>
                        ))}
                    </section>
                </>
            )}
        </div>
    );
};

export default Regras;
